import { PartnerCompanySelect } from '@/features/partner-companies/components/PartnerCompanySelect';
import type { PartnerCompanyOption } from '@/features/partner-companies/types/partner-company.types';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import { FpStructuredProfileFields } from '@/shared/components/fp-profile/FpStructuredProfileFields';
import type { AdminAdvisorEditorDetail } from '../../types/admin-advisor.type';
import type { AdvisorEditorFormData } from './advisorEditor.types';
import { AdvisorEditorSection } from './AdvisorEditorSection';
import { Field, ReadOnly, TextAreaField } from './advisorEditorFields';
import { AdvisorPhotoField } from './AdvisorPhotoField';
import { AdvisorDesignationsSection } from './AdvisorDesignationsSection';
import { AdvisorTrialSection } from './AdvisorTrialSection';
import { CommunicationAssetSection } from './CommunicationAssetSection';
import { SessionCreditSection } from '../care-concierge/SessionCreditSection';
import { CareConciergeToggle } from '../care-concierge/CareConciergeToggle';
import { CareConciergeFeeField } from '../care-concierge/CareConciergeFeeField';
import { UnsavedChangesBanner } from '../provider-editor/UnsavedChangesBanner';

const BIOGRAPHY_MAX_LENGTH = 1000;

type AdvisorInformationTabProps = {
  advisor: AdminAdvisorEditorDetail;
  formData: AdvisorEditorFormData;
  errors: Partial<Record<keyof AdvisorEditorFormData, string>>;
  isDirty: boolean;
  onChange: <K extends keyof AdvisorEditorFormData>(
    field: K,
    value: AdvisorEditorFormData[K],
  ) => void;
  onPhotoUploadingChange?: (uploading: boolean) => void;
};

const accountStatuses = ['ACTIVE', 'INACTIVE', 'PENDING', 'SUSPENDED'] as const;

export const AdvisorInformationTab = ({
  advisor,
  formData,
  errors,
  isDirty,
  onChange,
  onPhotoUploadingChange,
}: AdvisorInformationTabProps) => {
  const partnerCompany = advisor.user.partnerCompany;
  const partnerOption: PartnerCompanyOption | null = partnerCompany
    ? {
        id: partnerCompany.id,
        name: partnerCompany.name,
        referralCode: partnerCompany.referralCode,
        logoUrl: partnerCompany.logoUrl,
      }
    : null;

  return (
    <div role="tabpanel" className="space-y-4">
      {isDirty && <UnsavedChangesBanner />}

      <AdvisorEditorSection title="Account Management">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="First Name" error={errors.firstName}>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(event) => onChange('firstName', event.target.value)}
            />
          </Field>
          <Field label="Last Name">
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(event) => onChange('lastName', event.target.value)}
            />
          </Field>
          <Field label="Email" error={errors.email}>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(event) => onChange('email', event.target.value)}
            />
          </Field>
          <Field label="Account Status">
            <select
              id="accountStatus"
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={formData.accountStatus}
              onChange={(event) =>
                onChange('accountStatus', event.target.value as AdvisorEditorFormData['accountStatus'])
              }
            >
              {accountStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </Field>
          <ReadOnly label="Advisor ID" value={advisor.id} />
          <ReadOnly label="Kinde ID" value={advisor.user.kindeId ?? 'Not linked'} />
        </div>
        <AdvisorPhotoField
          value={formData.profilePhoto}
          error={errors.profilePhoto}
          onChange={(value) => onChange('profilePhoto', value)}
          onUploadingChange={onPhotoUploadingChange}
        />
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Service Provider Visibility">
        <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
          <div>
            <Label htmlFor="hideFromServiceProviders">Hide from Service Providers</Label>
            <p className="text-sm text-gray-600">
              Admin-only visibility control. Saving this does not send an email.
            </p>
          </div>
          <Switch
            id="hideFromServiceProviders"
            checked={formData.hideFromServiceProviders}
            onCheckedChange={(value) => onChange('hideFromServiceProviders', value)}
          />
        </div>
      </AdvisorEditorSection>

      <AdvisorDesignationsSection
        formData={formData}
        error={errors.firmSizeSegment}
        mostRecentDesignationChange={advisor.mostRecentDesignationChange}
        onChange={onChange}
      />

      {/* Feature 100 — trial lifecycle controls (action-based, outside the PATCH form) */}
      <AdvisorTrialSection
        advisorId={advisor.id}
        initialTrial={{
          isTrial: advisor.isTrial ?? false,
          trialExpiresAt: advisor.trialExpiresAt ?? null,
          trialConvertedAt: advisor.trialConvertedAt ?? null,
        }}
      />

      {/* Feature 106 — this account's own Care Concierge sessions. Separate from any firm pool
          they may also draw on; an advisor with no firm has only this one. Keyed on the user
          rather than the advisor profile, because credit belongs to the account. Append-only, and
          deliberately outside the PATCH form for the same reason as the trial controls. */}
      <AdvisorEditorSection title="Care Concierge Sessions">
        {/* The switch is keyed on the advisor profile, the credit on the account: the card only
            exists for advisors, while credit belongs to a person. Both are shown together because
            they are agreed together. */}
        <CareConciergeToggle owner="advisor" ownerId={advisor.id} />
        <CareConciergeFeeField owner="advisor" ownerId={advisor.id} />
        <SessionCreditSection ownerType="USER" ownerId={advisor.user.id} />
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Basic Information">
        <Field label="Company / Firm Name" error={errors.companyName}>
          <Input
            id="companyName"
            value={formData.companyName}
            onChange={(event) => onChange('companyName', event.target.value)}
          />
        </Field>
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Partner Assignment">
        <PartnerCompanySelect
          label="Partner Company"
          value={partnerOption}
          onChange={(option) => onChange('partnerCompanyId', option?.id ?? null)}
          placeholder="Search partner companies..."
        />
        {(partnerCompany?.ssoEnabled || formData.partnerCompanyId) && (
          <p className="mt-2 text-xs text-amber-700">
            If the selected partner company has SSO enabled, saving will clear the advisor&apos;s
            current identity link so their next enterprise sign-in can relink the account.
          </p>
        )}
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Advisor Biography">
        <div className="space-y-1">
          <Label htmlFor="advisorBiography">Biography (shown above Company Overview on the public profile)</Label>
          <Textarea
            id="advisorBiography"
            value={formData.advisorBiography}
            onChange={(event) => onChange('advisorBiography', event.target.value)}
            aria-describedby="advisorBiography-counter"
            aria-invalid={
              formData.advisorBiography.length > BIOGRAPHY_MAX_LENGTH || !!errors.advisorBiography
            }
          />
          <p
            id="advisorBiography-counter"
            className={
              formData.advisorBiography.length > BIOGRAPHY_MAX_LENGTH
                ? 'text-xs text-red-600'
                : 'text-xs text-gray-500'
            }
            aria-live="polite"
          >
            {formData.advisorBiography.length} / {BIOGRAPHY_MAX_LENGTH}
          </p>
          {errors.advisorBiography && (
            <p className="text-sm text-red-600">{errors.advisorBiography}</p>
          )}
        </div>
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Address">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Street Address">
            <Input
              id="addressLine1"
              value={formData.addressLine1}
              onChange={(event) => onChange('addressLine1', event.target.value)}
            />
          </Field>
          <Field label="Apt / Suite (optional)">
            <Input
              id="addressLine2"
              value={formData.addressLine2}
              onChange={(event) => onChange('addressLine2', event.target.value)}
            />
          </Field>
          <Field label="City">
            <Input
              id="addressCity"
              value={formData.addressCity}
              onChange={(event) => onChange('addressCity', event.target.value)}
            />
          </Field>
          <Field label="State (2-letter)" error={errors.addressState}>
            <Input
              id="addressState"
              value={formData.addressState}
              maxLength={2}
              onChange={(event) => onChange('addressState', event.target.value.toUpperCase())}
              aria-invalid={!!errors.addressState}
              aria-errormessage={errors.addressState ? 'addressState-error' : undefined}
            />
          </Field>
          <Field label="ZIP">
            <Input
              id="addressZip"
              value={formData.addressZip}
              onChange={(event) => onChange('addressZip', event.target.value)}
            />
          </Field>
        </div>
      </AdvisorEditorSection>

      <AdvisorEditorSection title="Profile Content">
        <TextAreaField label="Company Overview" value={formData.companyOverview} onChange={(value) => onChange('companyOverview', value)} />
        <FpStructuredProfileFields
          clientSegments={formData.predominantClientSegments}
          investableAssets={formData.investableAssets}
          serviceOfferings={formData.serviceOfferingsList}
          hasLegacyServiceOfferings={
            !!formData.legacyServiceOfferings && formData.serviceOfferingsList.length === 0
          }
          onClientSegmentsChange={(value) => onChange('predominantClientSegments', value)}
          onInvestableAssetsChange={(value) => onChange('investableAssets', value)}
          onServiceOfferingsChange={(value) => onChange('serviceOfferingsList', value)}
        />
        <TextAreaField label="Ideal Client Profile" value={formData.idealClientProfile} onChange={(value) => onChange('idealClientProfile', value)} />
        <TextAreaField label="Differentiating Factors" value={formData.differentiatingFactors} onChange={(value) => onChange('differentiatingFactors', value)} />
      </AdvisorEditorSection>

      <CommunicationAssetSection fpId={advisor.id} />
    </div>
  );
};
